import React, { useEffect, useState } from "react";

import { AlertTriangle, Loader2Icon } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { supabase } from "~/integrations/supabase/client";
import { Profile } from "~/pages/admin/types";

interface ClientAssignment {
  client_id: number;
  client_name: string;
  client_company: string;
}

interface DeleteUserDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  profile: Profile | null;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
}

export const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({
  isOpen,
  onOpenChange,
  profile,
  onConfirm,
  isDeleting,
}) => {
  const [clientAssignments, setClientAssignments] = useState<
    ClientAssignment[]
  >([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Check for client assignments when dialog opens for a seller
  useEffect(() => {
    const checkClientAssignments = async () => {
      if (!isOpen || !profile || profile.role !== "seller") {
        setClientAssignments([]);
        return;
      }

      setLoadingAssignments(true);
      try {
        const { data, error } = await supabase
          .from("clients")
          .select("id, name, company")
          .eq("assigned_seller", profile.id);

        if (error) {
          console.error("Error fetching client assignments:", error);
          setClientAssignments([]);
        } else {
          const assignments = (data || []).map((client) => ({
            client_id: client.id,
            client_name: client.name,
            client_company: client.company,
          }));
          setClientAssignments(assignments);
        }
      } catch (error) {
        console.error("Error checking client assignments:", error);
        setClientAssignments([]);
      } finally {
        setLoadingAssignments(false);
      }
    };

    checkClientAssignments();
  }, [isOpen, profile]);

  const hasClientAssignments = clientAssignments.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Eliminar Usuario</DialogTitle>
          <DialogDescription>
            ¿Estás seguro que deseas eliminar al usuario{" "}
            <strong>
              {profile?.name} {profile?.lastname}
            </strong>
            ? Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>

        {/* Loading state for assignments check */}
        {loadingAssignments && profile?.role === "seller" && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2Icon className="h-4 w-4 animate-spin" />
            Verificando asignaciones de clientes...
          </div>
        )}

        {/* Warning for sellers with client assignments */}
        {!loadingAssignments && hasClientAssignments && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <div className="mb-2 font-medium">
                Este vendedor tiene {clientAssignments.length} cliente
                {clientAssignments.length !== 1 ? "s" : ""} asignado
                {clientAssignments.length !== 1 ? "s" : ""}:
              </div>
              <ul className="max-h-32 space-y-1 overflow-y-auto text-sm">
                {clientAssignments.slice(0, 5).map((assignment) => (
                  <li
                    key={assignment.client_id}
                    className="flex items-center gap-1"
                  >
                    • {assignment.client_name} ({assignment.client_company})
                  </li>
                ))}
                {clientAssignments.length > 5 && (
                  <li className="font-medium text-amber-600">
                    ... y {clientAssignments.length - 5} más
                  </li>
                )}
              </ul>
              <div className="mt-2 text-sm">
                <strong>
                  Al eliminar este vendedor, todos los clientes asignados
                  quedarán sin vendedor asignado.
                </strong>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting || loadingAssignments}
          >
            {isDeleting ? (
              <>
                <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                Eliminando...
              </>
            ) : (
              "Eliminar"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
